import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button } from '@components/Common/Button';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { Info } from '@components/Common/SharedStyles';
import { SidebarButtons, SideForm } from '@components/Common/SideBarLayout';
import { Terminal } from '@api/queries/terminals';

// TanStack Query hooks for Users
import { useUpdateUserTerminalsMutation, User } from '@api/queries/users';

const LabelStyled = styled.label`
  margin-left: 25px;
  margin-bottom: 3px;
  display: block;
`;

const InputStyled = styled.input`
  margin-right: 10px;
`;

interface Props {
  selectedUser: User;
  terminals: Terminal[];
  onClose: () => void;
}

const UserEditor: React.FC<Props> = ({
  selectedUser,
  terminals,
  onClose
}) => {
  const { t } = useTranslation();

  // Local state for terminal selection
  const [selectedTerminalIds, setSelectedTerminalIds] = useState<string[]>(
    selectedUser?.terminals || []
  );

  // TanStack Query mutation
  const updateTerminalsMutation = useUpdateUserTerminalsMutation();
  const isSaving = updateTerminalsMutation.isPending;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    const terminalId = target.value;

    if (target.checked) {
      setSelectedTerminalIds(prev => [...prev, terminalId]);
    } else {
      setSelectedTerminalIds(prev => prev.filter(c => c !== terminalId));
    }
  };

  const onSubmit = (e: React.FormEvent<any>) => {
    e.preventDefault();

    updateTerminalsMutation.mutate(
      { userId: selectedUser.id, terminals: selectedTerminalIds },
      { onSuccess: () => onClose() }
    );
  };

  const isChecked = (terminalId: string) => {
    return selectedTerminalIds.some(t => t === terminalId);
  };

  if (!selectedUser) {
    return (
      <div>
        {t('users.select_a_user')}
        ...
      </div>
    );
  }

  return (
    <SideForm onSubmit={onSubmit} onEscape={onClose}>
      <h3>{t('users.users_terminals')}</h3>
      <div>
        <Info>{t('users.info_default_terminal')}</Info>
      </div>
      <p></p>
      {terminals.map((p, i) => (
        <LabelStyled key={p.id}>
          <InputStyled
            autoFocus={i === 0}
            value={p.id}
            name="terminal"
            type="checkbox"
            checked={isChecked(p.id)}
            onChange={handleInputChange}
          />
          {p.name}
        </LabelStyled>
      ))}
      <SidebarButtons>
        <Button type="button" secondary={true} onClick={onClose}>
          {t('cancel')}
        </Button>
        <div />
        {isSaving && <OperationLoader title={t('saving') + '...'} />}
        <Button type="submit" tertiary={true} disabled={isSaving}>
          {t('save')}
        </Button>
      </SidebarButtons>
    </SideForm>
  );
};

export default UserEditor;
